import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appPath } from "@/lib/paths";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getTasks, createTask, updateTask, setTaskStatus, deleteTask,
  getOperators, getCCPs, TASK_LINK_SECTIONS,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, ListChecks, ArrowRight, User, CalendarDays, Repeat, ShieldAlert } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const emptyTask = { id: null, title: "", description: "", operator_id: null, due_date: "", link_section: null, ccp_id: null, recurrence: "none", recurrence_interval: 1, recurrence_unit: "days", recurrence_weekdays: [] };
const NONE = "__none__";
const ALL = "__all__";
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6]; // Mon … Sun
const DAY_KEYS = ["tasks.dayMon", "tasks.dayTue", "tasks.dayWed", "tasks.dayThu", "tasks.dayFri", "tasks.daySat", "tasks.daySun"];
const sectionPath = (value) => (TASK_LINK_SECTIONS.find((s) => s.value === value) || {}).path;
const recurrenceLabel = (t, tk) => {
  const r = tk.recurrence || "none";
  if (r === "none") return null;
  if (r === "custom") {
    const n = tk.recurrence_interval || 1;
    const unit = t(tk.recurrence_unit === "weeks" ? "tasks.unitWeeks" : "tasks.unitDays", { n });
    return t("tasks.recurEvery", { interval: `${n} ${unit}` });
  }
  if (r === "working_days") return t("tasks.recur_working_days");
  if (r === "weekdays") {
    const days = (tk.recurrence_weekdays || []).slice().sort((a, b) => a - b).map((d) => t(DAY_KEYS[d])).join(", ");
    return days ? `${t("tasks.recur_weekdays")}: ${days}` : t("tasks.recur_weekdays");
  }
  return t(`tasks.recur_${r}`);
};

export default function Tasks() {
  const { t } = useT();
  const { can } = useAuth();
  const navigate = useNavigate();
  const canManage = can("admin", "manager");
  const [tasks, setTasks] = useState([]);
  const [operators, setOperators] = useState([]);
  const [ccps, setCcps] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [opFilter, setOpFilter] = useState(ALL);

  const load = async () => setTasks(await getTasks());
  useEffect(() => {
    (async () => {
      await load();
      try { setOperators(await getOperators({ active: true })); }
      catch (e) { console.error("Tasks: failed to load operators", e); }
      try { setCcps(await getCCPs()); }
      catch (e) { console.error("Tasks: failed to load CCPs", e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = tasks.filter((tk) => {
    if (statusFilter !== "all" && tk.status !== statusFilter) return false;
    if (opFilter !== ALL && tk.operator_id !== opFilter) return false;
    return true;
  });

  const save = async (f) => {
    try {
      if (!f.title?.trim()) { toast.error(t("tasks.saveFailed")); return; }
      const payload = {
        title: f.title, description: f.description,
        operator_id: f.operator_id || null,
        due_date: f.due_date || null,
        link_section: f.link_section || null,
        ccp_id: f.ccp_id || null,
        recurrence: f.recurrence || "none",
        recurrence_interval: f.recurrence === "custom" ? Math.max(parseInt(f.recurrence_interval, 10) || 1, 1) : null,
        recurrence_unit: f.recurrence === "custom" ? (f.recurrence_unit || "days") : null,
        recurrence_weekdays: f.recurrence === "weekdays" ? (f.recurrence_weekdays || []) : null,
      };
      if (f.recurrence === "weekdays" && !(f.recurrence_weekdays || []).length) {
        toast.error(t("tasks.pickDays"));
        return;
      }
      if (f.id) await updateTask(f.id, payload);
      else await createTask(payload);
      toast.success(f.id ? t("tasks.updated") : t("tasks.created"));
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("tasks.saveFailed")); }
  };

  const toggle = async (tk) => {
    try {
      const nowDone = tk.status !== "done";
      await setTaskStatus(tk.id, nowDone ? "done" : "open");
      toast.success(t("tasks.statusUpdated"));
      // Completing a CCP-linked task opens the HACCP logging form so the reading is recorded.
      if (nowDone && tk.ccp_id) {
        navigate(appPath(`/haccp?ccp=${tk.ccp_id}`));
        return;
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("tasks.confirmDelete"))) return;
    try { await deleteTask(id); toast.success(t("tasks.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  return (
    <Layout actions={
      canManage ? (
        <Button size="sm" onClick={() => setDialog({ ...emptyTask })} data-testid="add-task-button">
          <Plus className="mr-1.5 h-4 w-4" /> {t("tasks.add")}
        </Button>
      ) : null
    }>
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <ListChecks className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("tasks.title")} ({filtered.length})
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-32" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("tasks.allTasks")}</SelectItem>
                <SelectItem value="open">{t("tasks.openTasks")}</SelectItem>
                <SelectItem value="done">{t("tasks.doneTasks")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={opFilter} onValueChange={setOpFilter}>
              <SelectTrigger className="h-8 w-40" data-testid="task-operator-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("tasks.filterOperator")}</SelectItem>
                {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="tasks-empty">
              <ListChecks className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("tasks.empty")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((tk) => (
                <div key={tk.id} data-testid={`task-row-${tk.id}`} className="flex items-start gap-3 rounded-lg border border-border p-3">
                  <Checkbox checked={tk.status === "done"} onCheckedChange={() => toggle(tk)} className="mt-0.5" data-testid={`task-toggle-${tk.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-medium ${tk.status === "done" ? "text-muted-foreground line-through" : ""}`}>{tk.title}</div>
                    {tk.description && <div className="mt-0.5 text-xs text-muted-foreground">{tk.description}</div>}
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <Badge className="bg-secondary text-muted-foreground border border-border gap-1">
                        <User className="h-3 w-3" /> {tk.operator_name || t("tasks.unassigned")}
                      </Badge>
                      {tk.due_date && (
                        <Badge className="badge-warn gap-1"><CalendarDays className="h-3 w-3" /> {t("tasks.dueOn", { date: tk.due_date })}</Badge>
                      )}
                      {recurrenceLabel(t, tk) && (
                        <Badge className="bg-secondary text-[hsl(var(--brand))] border border-border gap-1" data-testid={`task-recur-${tk.id}`}>
                          <Repeat className="h-3 w-3" /> {recurrenceLabel(t, tk)}
                        </Badge>
                      )}
                      {tk.auto_from_ccp && (
                        <Badge variant="secondary" className="gap-1" data-testid={`task-auto-${tk.id}`}>
                          <ShieldAlert className="h-3 w-3" /> {t("tasks.autoFromCcp")}
                        </Badge>
                      )}
                      {tk.ccp_id && (
                        <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[hsl(var(--danger))]" onClick={() => navigate(appPath(`/haccp?ccp=${tk.ccp_id}`))} data-testid={`task-ccp-${tk.id}`}>
                          <ShieldAlert className="h-3 w-3" /> {t("tasks.ccpLog", { name: tk.ccp_name || t("tasks.ccp") })} <ArrowRight className="ml-0.5 h-3 w-3" />
                        </Button>
                      )}
                      {tk.link_section && sectionPath(tk.link_section) && (
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[hsl(var(--brand))]" onClick={() => navigate(appPath(sectionPath(tk.link_section)))} data-testid={`task-link-${tk.id}`}>
                          {t("tasks.goTo", { section: t(`nav.${tk.link_section}`) })} <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      )}
                      {tk.status === "done" && tk.completed_by && (
                        <span className="text-xs text-muted-foreground">{t("tasks.completedBy", { name: tk.completed_by })}</span>
                      )}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex shrink-0 items-center">
                      <Button variant="ghost" size="icon" onClick={() => setDialog({ ...emptyTask, ...tk, due_date: tk.due_date || "" })} data-testid={`task-edit-${tk.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(tk.id)} data-testid={`task-delete-${tk.id}`}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <TaskDialog
        key={dialog ? (dialog.id ?? "new") : "closed"}
        dialog={dialog}
        operators={operators}
        ccps={ccps}
        onClose={() => setDialog(null)}
        onSave={save}
      />
    </Layout>
  );
}

function TaskDialog({ dialog, operators, ccps, onClose, onSave }) {
  const { t } = useT();
  const [f, setF] = useState(dialog);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{f.id ? t("tasks.edit") : t("tasks.add")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("tasks.taskTitle")}</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("tasks.description")}</Label>
            <Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="task-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("tasks.assignTo")}</Label>
              <Select value={f.operator_id || NONE} onValueChange={(v) => set("operator_id", v === NONE ? null : v)}>
                <SelectTrigger data-testid="task-assign-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t("tasks.unassigned")}</SelectItem>
                  {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("tasks.dueDate")}</Label>
              <Input type="date" value={f.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="task-due-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("tasks.linkTo")}</Label>
            <Select value={f.link_section || NONE} onValueChange={(v) => set("link_section", v === NONE ? null : v)}>
              <SelectTrigger data-testid="task-link-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("tasks.noLink")}</SelectItem>
                {TASK_LINK_SECTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{t(`nav.${s.value}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><ShieldAlert className="h-3.5 w-3.5 text-[hsl(var(--danger))]" /> {t("tasks.ccpField")}</Label>
            <Select value={f.ccp_id || NONE} onValueChange={(v) => set("ccp_id", v === NONE ? null : v)}>
              <SelectTrigger data-testid="task-ccp-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("tasks.noCcp")}</SelectItem>
                {(ccps || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.process_step ? ` · ${c.process_step}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {f.ccp_id && <p className="text-[11px] text-muted-foreground">{t("tasks.ccpHint")}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>{t("tasks.repeat")}</Label>
            <Select value={f.recurrence || "none"} onValueChange={(v) => set("recurrence", v)}>
              <SelectTrigger data-testid="task-recurrence-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("tasks.recur_none")}</SelectItem>
                <SelectItem value="daily">{t("tasks.recur_daily")}</SelectItem>
                <SelectItem value="working_days">{t("tasks.recur_working_days")}</SelectItem>
                <SelectItem value="weekdays">{t("tasks.recur_weekdays")}</SelectItem>
                <SelectItem value="weekly">{t("tasks.recur_weekly")}</SelectItem>
                <SelectItem value="monthly">{t("tasks.recur_monthly")}</SelectItem>
                <SelectItem value="custom">{t("tasks.recur_custom")}</SelectItem>
              </SelectContent>
            </Select>
            {f.recurrence === "weekdays" && (
              <div className="flex flex-wrap gap-1.5 pt-2" data-testid="task-weekday-picker">
                {WEEKDAYS.map((d) => {
                  const active = (f.recurrence_weekdays || []).includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      data-testid={`task-weekday-${d}`}
                      aria-pressed={active}
                      onClick={() => {
                        const cur = new Set(f.recurrence_weekdays || []);
                        active ? cur.delete(d) : cur.add(d);
                        set("recurrence_weekdays", [...cur].sort((a, b) => a - b));
                      }}
                      className={`h-9 w-11 rounded-lg border text-xs font-medium transition-colors ${active
                        ? "border-transparent bg-[hsl(var(--brand))] text-white"
                        : "border-border bg-card text-muted-foreground hover:bg-secondary"}`}
                    >
                      {t(DAY_KEYS[d])}
                    </button>
                  );
                })}
              </div>
            )}
            {f.recurrence === "custom" && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-sm text-muted-foreground">{t("tasks.every")}</span>
                <Input type="number" min="1" step="1" value={f.recurrence_interval ?? 1}
                  onChange={(e) => set("recurrence_interval", e.target.value)}
                  className="w-20 text-right" data-testid="task-recurrence-interval" />
                <Select value={f.recurrence_unit || "days"} onValueChange={(v) => set("recurrence_unit", v)}>
                  <SelectTrigger className="w-28" data-testid="task-recurrence-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">{t("tasks.unitDaysPlain")}</SelectItem>
                    <SelectItem value="weeks">{t("tasks.unitWeeksPlain")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(f.recurrence && f.recurrence !== "none") && (
              <p className="text-[11px] text-muted-foreground">{t("tasks.repeatHint")}</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="task-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
